import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    TextField,
    Paper,
    Popper,
    ClickAwayListener,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Typography,
    InputAdornment,
    FormHelperText,
    FormControl
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CustomArrowIcon } from 'components/CommonIcons/Icons';

interface MultiSelectWithCheckboxProps {
    value: string[];
    options: string[];
    placeholder?: string;
    label?: string;
    allOptionLabel?: string;
    onChange: (value: string[]) => void;
    disabled?: boolean;
    sx?: object;
    icon?: React.ReactNode;
    error?: boolean;
    helperText?: string;
}

const MultiSelectWithCheckbox = ({
    value = [],
    options,
    placeholder = 'Select',
    label,
    allOptionLabel,
    disabled,
    onChange,
    sx: sxOverride,
    icon,
    error = false,
    helperText
}: MultiSelectWithCheckboxProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const allValues = options.map(option => option);
    const theme = useTheme();
    const colors = theme.palette;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAllOptionClick = () => {
        if (value.length === allValues.length) {
            onChange([]);
        } else {
            onChange(allValues);
        }
    };

    const handleOptionClick = (option: string) => {
        if (allOptionLabel && option === allOptionLabel) {
            handleAllOptionClick();
        } else {
            const newValue = value.includes(option)
                ? value.filter(item => item !== option)
                : [...value, option];
            onChange(newValue);
        }
    };

    const displayText = value.length > 0 
        ? (value.length === allValues.length && allOptionLabel ? allOptionLabel : value.join(', '))
        : placeholder;

    return (
        <FormControl fullWidth error={error}>
            <TextField
                value={displayText}
                onChange={() => {}}
                onClick={handleOpen}
                disabled={disabled}
                placeholder={placeholder}
                label={label}
                variant="outlined"
                size="medium"
                error={error}
                InputProps={{
                    readOnly: true,
                    sx: {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        '& .MuiOutlinedInput-input': {
                            color: value.length > 0 ? '#000' : '#999',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }
                        ,
                        '& .clear-icon': {
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 150ms ease'
                        },
                        '&:hover .clear-icon, &:focus-within .clear-icon': {
                            opacity: 1,
                            pointerEvents: 'auto'
                        }
                    },
                    endAdornment: (
                        <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {value.length > 0 && !disabled && (
                                <Box component="span" className="clear-icon" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        component="button"
                                        type="button"
                                        onMouseDown={(e: any) => { e.stopPropagation(); e.preventDefault(); }}
                                        onClick={(e: any) => { e.stopPropagation(); onChange([]); }}
                                        aria-label="clear"
                                        sx={{
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 18, color: '#009BD8' }} />
                                    </Box>
                                </Box>
                            )}
                            <CustomArrowIcon
                                size={20}
                                color={disabled ? '#71758F' : '#009BD8'}
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        paddingRight: '0 !important',
                    },
                    '& .MuiInputBase-input': {
                        paddingRight: '8px !important',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colors.inputNew.inputBorder} !important`,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colors.inputNew.inputBorderFocus} !important`,
                    },
                    '& .MuiInputLabel-root': {
                        color: `${colors.input.text} !important`,
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                        color: `${colors.input.text} !important`,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#71758F0D !important',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e0e0e0 !important',
                        },
                        '& .MuiInputBase-input': {
                            WebkitTextFillColor: '#71758F !important',
                            color: '#71758F !important',
                        }
                    },
                    ...sxOverride
                }}
            />
            
            {helperText && <FormHelperText>{helperText}</FormHelperText>}

            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{
                    zIndex: 1300,
                    width: anchorEl ? anchorEl.clientWidth : 'auto',
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper
                        elevation={0}
                        sx={{
                            '&&': {
                                width: anchorEl ? anchorEl.clientWidth : 'auto',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                border: `1px solid ${colors.inputNew.inputBorder}`,
                                boxShadow: '0px 10px 30px rgba(0,0,0,0.12)',
                                backgroundColor: '#FFFFFF',
                                marginTop: '8px',
                            },
                        }}
                    >
                        <List
                            sx={{
                                '&&': {
                                    py: 0,
                                    maxHeight: '300px',
                                    overflow: 'auto',
                                    backgroundColor: '#FFFFFF',
                                },
                            }}
                        >
                            {allOptionLabel && (
                                <ListItemButton
                                    onClick={() => handleOptionClick(allOptionLabel)}
                                    sx={{
                                        '&&': {
                                            px: 2,
                                            py: 1,
                                            backgroundColor: value.length === allValues.length ? '#EAF4FF' : '#FFFFFF',
                                            '&:hover': {
                                                backgroundColor: value.length === allValues.length ? '#DCEEFF' : '#F5F5F5',
                                            },
                                            borderBottom: `1px solid #F0F0F0`,
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            '&&': {
                                                minWidth: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={value.length === allValues.length}
                                            tabIndex={-1}
                                            disableRipple
                                            sx={{
                                                '&&': {
                                                    '& .MuiSvgIcon-root': {
                                                        color: '#0095FF'
                                                    }
                                                },
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={allOptionLabel}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: '#000',
                                            },
                                        }}
                                    />
                                </ListItemButton>
                            )}

                            {options.map((option) => (
                                <ListItemButton
                                    key={option}
                                    onClick={() => handleOptionClick(option)}
                                    sx={{
                                        '&&': {
                                            px: 2,
                                            py: 1,
                                            backgroundColor: value.includes(option) ? '#EAF4FF' : '#FFFFFF',
                                            '&:hover': {
                                                backgroundColor: value.includes(option) ? '#DCEEFF' : '#F5F5F5',
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            '&&': {
                                                minWidth: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            edge="start"
                                            checked={value.includes(option)}
                                            tabIndex={-1}
                                            disableRipple
                                            sx={{
                                                '&&': {
                                                    '& .MuiSvgIcon-root': {
                                                        color: '#0095FF'
                                                    }
                                                },
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={option}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: '#000',
                                            },
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </FormControl>
    );
};

export default MultiSelectWithCheckbox;